import { useState, useEffect, useCallback } from "react";
import { useProfile } from "@/hooks/useProfile";
import { useUserStore } from "@/hooks/useUserStore";
import { useAuthStore } from "@/hooks/useAuthStore";
import { observer } from "mobx-react-lite";
import { useQueryClient } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { ChatbotSettingsSection } from "@/components/settings/ChatbotSettingsSection";
import { ProfileAvatarSection } from "@/components/settings/ProfileAvatarSection";
import { SettingsSection, SettingsSections } from "@/components/settings/SettingsSection";
import { normalizeHex } from "@/lib/avatarColors";
import { normalizeName } from "@/utils/sanitization";
import { pageInfoTarget } from "@/components/page-info/pageInfoTarget";

// Profile columns are nullable; the inputs are always controlled.
const text = (value: string | null | undefined): string => value ?? "";
const flag = (value: boolean | null | undefined): boolean => value ?? false;

export const PersonalSettingsForm = observer(function PersonalSettingsForm() {
  const { userId } = useAuth();
  const userStore = useUserStore();
  const authStore = useAuthStore();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  // Load profile if needed
  useProfile();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [company, setCompany] = useState("");
  const [color, setColor] = useState("");
  const [pmDigestOptOut, setPmDigestOptOut] = useState(false);
  const [messageBoardOptOut, setMessageBoardOptOut] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [personalLoading, setPersonalLoading] = useState(false);

  // Local form state mirrors the stored profile — used to seed the form, and to
  // discard edits when the user cancels.
  const resetFromProfile = useCallback(() => {
    const profile = userStore.userProfile;
    if (!profile) return;
    setFirstName(text(profile.first_name));
    setLastName(text(profile.last_name));
    setEmail(text(profile.email));
    setPhoneNumber(text(profile.phone_number));
    setJobTitle(text(profile.job_title));
    setCompany(text(profile.company));
    setColor(text(profile.color));
    setPmDigestOptOut(flag(profile.pm_digest_opt_out));
    setMessageBoardOptOut(flag(profile.message_board_opt_out));
  }, [userStore.userProfile]);

  // Seed local form state from the store once the profile has loaded.
  useEffect(() => {
    if (userStore.userProfile && !initialized) {
      resetFromProfile();
      setInitialized(true);
    }
  }, [userStore.userProfile, initialized, resetFromProfile]);

  const handlePersonalSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setPersonalLoading(true);

    try {
      if (!userId) {
        throw new Error(t.settings.personal.notAuthenticated);
      }

      if (!userStore.userProfile) {
        throw new Error(t.settings.personal.profileNotLoaded);
      }

      // normalizeName, not .trim(): a zero-width space or Hangul filler survives trim()
      // and would store a name that renders as nothing — the same hole signup closes.
      const normalizedFirstName = normalizeName(firstName);
      const normalizedLastName = normalizeName(lastName);

      if (!normalizedFirstName || !normalizedLastName) {
        throw new Error(t.settings.personal.namesRequired);
      }

      // Validate the avatar color: normalize to #rrggbb, or reject. An empty
      // field leaves the stored color untouched.
      let normalizedColor: string | undefined;
      if (color.trim()) {
        const nc = normalizeHex(color);
        if (!nc) {
          throw new Error(t.settings.personal.invalidColor);
        }
        normalizedColor = nc;
      }

      const success = await userStore.updateProfile(userId, {
        first_name: normalizedFirstName,
        last_name: normalizedLastName,
        phone_number: phoneNumber.trim() || undefined,
        job_title: jobTitle.trim() || undefined,
        company: company.trim() || undefined,
        pm_digest_opt_out: pmDigestOptOut,
        message_board_opt_out: messageBoardOptOut,
        ...(normalizedColor ? { color: normalizedColor } : {}),
      });

      if (!success) {
        throw new Error(userStore.error || "Failed to update profile");
      }

      // Update auth user email if changed
      if (email && email !== userStore.userProfile.email) {
        const success = await authStore.updateEmail(email);
        if (!success) {
          throw new Error(authStore.error || "Failed to update email");
        }
      }

      await queryClient.invalidateQueries({ queryKey: ["user-profile", userId] });

      toast({
        title: t.settings.personal.profileUpdated,
        description: t.settings.personal.profileUpdatedDesc,
      });
    } catch (error) {
      console.error("Error updating profile:", error);
      toast({
        variant: "destructive",
        title: t.settings.personal.updateFailed,
        description: error instanceof Error ? error.message : t.settings.personal.updateFailedDesc,
      });
    } finally {
      setPersonalLoading(false);
    }
  };

  return (
    <form onSubmit={handlePersonalSave}>
      <SettingsSections>
        <ProfileAvatarSection color={color} onColorChange={setColor} />

        <SettingsSection label={t.settings.personal.basicInfo}>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">{t.settings.personal.firstName}</Label>
              <Input
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">{t.settings.personal.lastName}</Label>
              <Input id="lastName" value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </div>
          </div>
        </SettingsSection>

        <SettingsSection label={t.settings.personal.contactInfo}>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t.settings.personal.email}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phoneNumber">{t.settings.personal.phone}</Label>
              <Input
                id="phoneNumber"
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder={t.settings.personal.phonePlaceholder}
              />
            </div>
          </div>
        </SettingsSection>

        <SettingsSection label={t.settings.personal.professionalInfo}>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="jobTitle">{t.settings.personal.jobTitle}</Label>
              <Input
                id="jobTitle"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                placeholder={t.settings.personal.jobTitlePlaceholder}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="company">{t.settings.personal.company}</Label>
              <Input
                id="company"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder={t.settings.personal.companyPlaceholder}
              />
            </div>
          </div>
        </SettingsSection>

        <SettingsSection
          label={t.settings.personal.notifications}
          {...pageInfoTarget("settings.notifications")}
        >
          <div className="flex items-start gap-3">
            <Checkbox
              id="pmDigestOptIn"
              checked={!pmDigestOptOut}
              onCheckedChange={(checked) => setPmDigestOptOut(!checked)}
              className="mt-0.5"
            />
            <div className="space-y-1">
              <Label htmlFor="pmDigestOptIn" className="cursor-pointer">
                {t.settings.personal.pmDigestLabel}
              </Label>
              <p className="text-sm text-muted-foreground">
                {t.settings.personal.pmDigestDescription}
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Checkbox
              id="messageBoardOptIn"
              checked={!messageBoardOptOut}
              onCheckedChange={(checked) => setMessageBoardOptOut(!checked)}
              className="mt-0.5"
            />
            <div className="space-y-1">
              <Label htmlFor="messageBoardOptIn" className="cursor-pointer">
                {t.settings.personal.messageBoardLabel}
              </Label>
              <p className="text-sm text-muted-foreground">
                {t.settings.personal.messageBoardDescription}
              </p>
            </div>
          </div>
        </SettingsSection>

        {/* Chatbot permissions save on toggle; they share the page, not this form's submit. */}
        <ChatbotSettingsSection />

        <div className="flex items-center justify-end gap-2 pt-6">
          <Button
            type="button"
            variant="ghost"
            onClick={resetFromProfile}
            disabled={personalLoading}
          >
            {t.common.cancel}
          </Button>
          <Button type="submit" disabled={personalLoading} {...pageInfoTarget("settings.save")}>
            {personalLoading ? t.common.saving : t.common.saveChanges}
          </Button>
        </div>
      </SettingsSections>
    </form>
  );
});
